'use client';

// BotPage - Main container component for public bot pages
// Story 4.1: Access Bot via Public Link
// Story 4.2: Chat Interface for Asking Questions
// Story 4.3: Real-Time Bot Responses
// Story 4.4: Attendee Feedback on Answer Quality
// Story 4.5: Mobile-Optimized Bot Interface

import { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import type { Bot } from '@boty/database';
import { BotHeader } from './BotHeader';
import { ChatInterface } from './ChatInterface';
import { ViralFooter } from './ViralFooter';
import { useChat } from './useChat';
import { useFeedback } from './useFeedback';
import { useSuggestedQuestions } from './useSuggestedQuestions';
import { useIntakeFlow } from './useIntakeFlow';
import { useCreatorMessage } from './useCreatorMessage';
import { getOrCreateSessionId } from '../../lib/session';

const INTAKE_PROMPT_DELAY_MS = 5000;

interface BotPageProps {
  bot: Bot;
  locale: string;
  isCreatorPreview?: boolean;
}

export function BotPage({ bot, locale: _locale, isCreatorPreview = false }: BotPageProps) {
  // Note: locale is available for future use (e.g., language-specific formatting)
  // RTL direction is handled by the theme provider in layout.tsx

  const [sessionId, setSessionId] = useState<string>('');

  // Initialize session on mount (client-side only)
  useEffect(() => {
    setSessionId(getOrCreateSessionId());
  }, []);

  const { messages, isLoading, error, sendMessage } = useChat({
    botId: bot.id,
    sessionId,
    enabled: !!sessionId,
    isCreatorPreview,
  });

  const { feedbackMap, submitFeedback } = useFeedback({
    botId: bot.id,
    sessionId,
    enabled: !!sessionId,
  });

  // Story 4.10: Suggested questions — always fetch once, persist below input
  const { questions: suggestedQuestions, isLoading: suggestedQuestionsLoading } = useSuggestedQuestions({
    botId: bot.id,
  });

  // Story 7.6: Intake questions flow
  const intakeEnabled = bot.intake_enabled === true;
  const intakeFlow = useIntakeFlow(intakeEnabled ? bot.id : '', sessionId);

  // Trigger intake prompt after a delay once there is at least one Q&A exchange
  const { hasQuestions, state: intakeState, startPrompt } = intakeFlow;
  useEffect(() => {
    if (!intakeEnabled || !hasQuestions || intakeState.status !== 'idle') return;
    if (messages.length < 2) return; // Wait for at least one Q&A exchange

    const timer = setTimeout(startPrompt, INTAKE_PROMPT_DELAY_MS);

    return () => clearTimeout(timer);
  }, [intakeEnabled, hasQuestions, intakeState.status, messages.length, startPrompt]);

  // Story 4.11: Creator message flow
  const creatorMsg = useCreatorMessage(bot.id, sessionId);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100dvh',
        bgcolor: 'background.default',
        // Safe-area inset support for notched phones (Story 4.5)
        pb: 'env(safe-area-inset-bottom, 0px)',
      }}
    >
      {/* Responsive container: full-width on mobile, max-width on larger screens */}
      <Container
        disableGutters
        maxWidth={false}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          maxWidth: { xs: '100%', md: 800, lg: 1000 },
          mx: 'auto',
          overflow: 'hidden',
        }}
      >
        <BotHeader name={bot.name} slug={bot.slug} description={bot.description} />

        <Box
          component="main"
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <ChatInterface
            botName={bot.name}
            customWelcomeMessage={bot.welcome_message}
            messages={messages}
            onSendMessage={sendMessage}
            isLoading={isLoading}
            error={error}
            feedbackMap={feedbackMap}
            onFeedbackSubmit={submitFeedback}
            suggestedQuestions={suggestedQuestions}
            suggestedQuestionsLoading={suggestedQuestionsLoading}
            intakeFlow={intakeEnabled ? intakeFlow : undefined}
            creatorName={bot.name}
            creatorMessage={creatorMsg}
          />
        </Box>

        <ViralFooter
          botId={bot.id}
          creatorId={bot.user_id ?? undefined}
        />
      </Container>
    </Box>
  );
}
